import schema from "@/platform/bridge/bridge-v1.schema.json"

type SchemaNode = Record<string, unknown>

const DEFS = (schema as unknown as { $defs: Record<string, SchemaNode> }).$defs

export const ENVELOPE_KEYS: ReadonlySet<string> = new Set(Object.keys(schema.properties))

const PAYLOAD_REFS: ReadonlyMap<string, string> = new Map(
  schema.$defs.payloadRules.allOf.map((rule) => [rule.if.properties.type.const as string, rule.then.properties.data.$ref as string])
)

export const ERROR_NODE = schema.properties.error as unknown as SchemaNode

const resolve = (node: SchemaNode): SchemaNode => {
  const ref = node.$ref
  if (typeof ref !== "string") return node

  const target = DEFS[ref.replace("#/$defs/", "")]
  if (target === undefined) throw new Error(`unresolvable $ref ${ref}`)

  return resolve(target)
}

const isPlainObject = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null && !Array.isArray(value)

const validateObject = (node: SchemaNode, value: unknown, path: string): string | null => {
  if (!isPlainObject(value)) return `${path} is not an object`

  const properties = (node.properties ?? {}) as Record<string, SchemaNode>
  const required = (node.required ?? []) as ReadonlyArray<string>

  for (const key of required) {
    if (!(key in value)) return `${path} is missing ${key}`
  }

  for (const [key, entry] of Object.entries(value)) {
    const child = properties[key]
    if (child === undefined) {
      if (node.additionalProperties === false) return `${path} does not declare ${key}`
      continue
    }

    const failure = validateNode(child, entry, `${path}.${key}`)
    if (failure !== null) return failure
  }

  return null
}

export const validateNode = (raw: SchemaNode, value: unknown, path: string): string | null => {
  const node = resolve(raw)

  if (Array.isArray(node.oneOf)) {
    const matches = (node.oneOf as SchemaNode[]).filter((candidate) => validateNode(candidate, value, path) === null)
    return matches.length === 1 ? null : `${path} must match exactly one variant`
  }

  if ("const" in node) return value === node.const ? null : `${path} is not ${String(node.const)}`

  if ("enum" in node) {
    const allowed = node.enum as ReadonlyArray<unknown>
    return allowed.includes(value) ? null : `${path} is not an allowed value`
  }

  switch (node.type) {
    case "object":
      return validateObject(node, value, path)
    case "array": {
      if (!Array.isArray(value)) return `${path} is not an array`

      const items = node.items as SchemaNode | undefined
      if (items === undefined) return null

      for (const [index, entry] of value.entries()) {
        const failure = validateNode(items, entry, `${path}[${index}]`)
        if (failure !== null) return failure
      }
      return null
    }
    case "string": {
      if (typeof value !== "string") return `${path} is not a string`

      const minLength = node.minLength
      if (typeof minLength === "number" && value.length < minLength) return `${path} is shorter than ${minLength}`

      const pattern = node.pattern
      if (typeof pattern === "string" && !new RegExp(pattern).test(value)) return `${path} does not match ${pattern}`

      return null
    }
    case "boolean":
      return typeof value === "boolean" ? null : `${path} is not a boolean`
    case "number":
      return typeof value === "number" && Number.isFinite(value) ? null : `${path} is not a number`
    default:
      return null
  }
}

export const validatePayload = (type: string, data: unknown, path: string): string | null => {
  const ref = PAYLOAD_REFS.get(type)
  if (ref === undefined) return null

  return validateNode({ $ref: ref }, data, path)
}
